import type { ApiClient } from './client';
import type { Me, Balance } from './types';
import type { RewardsResponse } from './rewards';

export interface ProfileResponse {
  spent_legacy_tokens?: number | null;
  stats: { made: number | null; spent: number | null; streak: number | null };
  weeks: unknown | null;
  counts: { voices: number | null; variants: number | null; people: number | null; fav: number | null };
  capabilities: {
    voices_count: boolean; people_count: boolean; made: boolean; spent: boolean;
    streak: boolean; weeks: boolean; variants_count: boolean; fav_count: boolean;
  };
}

export interface SettingsResponse {
  notifications_version?: string | null;
  default_ratio: string | null;
  default_voice_id: number | null;
  speech_rate: number | null;
  autoplay: boolean | null;
  send_to_chat: boolean | null;
  notifications: unknown | null;
  vibration: boolean | null;
  news_seen: string[] | null;
  sources: { bot_notifications?: { version: string } | null; product_settings: { version: string } | null; voice_preferences: {
    version: string; lang: string; currency: string; platform: string | null;
    recent_voice_id: number | null; tts_voice_id: number | null;
  } };
  capabilities: {
    product_settings_read: boolean; product_settings_write: boolean;
    notifications_read: boolean; notifications_write: boolean;
    news_seen: boolean; voice_preferences: boolean;
  };
}

export interface SettingsPatch {
  news_seen?: string[];
  default_ratio?: '1:1' | '3:4' | '4:5' | '9:16' | '16:9';
  default_voice_id?: number | null;
  speech_rate?: number;
  autoplay?: boolean;
  vibration?: boolean;
  send_to_chat?: boolean;
}

/** Страницы аккаунта читают только подтверждённые поля; отсутствие данных остаётся null. */
export const account = (client: ApiClient) => ({
  news: (signal?: AbortSignal) => client.request<NewsRelease>('/app/news', { signal }),
  clearHistory: (key: string) => client.request<{ cleared: boolean; works: number; orders: number }>('/app/history', { method: 'DELETE', idempotency: key }),
  onboardingSeen: (how: string) => client.request<void>('/app/me/onboarding/seen', { method: 'POST', body: { content_version: 'v1', how } }),
  initialQuestionSeen: () => client.request<void>('/app/me/initial-question/seen', { method: 'POST', body: { content_version: 'v1' } }),
  hintSeen: (key: string) => client.request<void>(`/app/me/hints/${encodeURIComponent(key)}`, { method: 'PUT', body: { content_version: 'v1' } }),
  telegramRefresh: () => client.request<Me>('/app/me/telegram/refresh', { method: 'POST' }),
  partner: () => client.request<{ link: string; code: string; friends_all: number; buyers_all: number; earned: number; paused: boolean }>('/app/partner'),
  identity: (kind: 'login' | 'register' | 'password-reset', body: { email: string; password?: string; consent?: boolean; operation_id?: string; receipt?: string }) =>
    client.request<{ linked: boolean; login?: string; since?: string; operation_id?: string; status?: string; confirmation_required?: boolean }>(`/app/steos-id/${kind}`, { method: 'POST', body }),
  mergeStatus: (id: string, receipt: string, signal?: AbortSignal) => client.request<{ operation_id: string; status: string; reauthentication_required: boolean }>(`/app/steos-id/merges/${id}`, { mergeReceipt: receipt, signal }),
  rewards: () => client.request<RewardsResponse>('/app/rewards'),
  claimReward: (id: string | null, key: string) => client.request(id ? `/app/rewards/tasks/${encodeURIComponent(id)}/claim` : '/app/rewards/streak/claim', { method: 'POST', idempotency: key }),
  works: (tab: 'all' | 'fav', cursor?: string, signal?: AbortSignal) =>
    client.request<{ items: ProfileWork[]; next_cursor: string | null }>('/app/orders', { query: { tab, limit: 20, cursor }, signal }),
  favorite: (id: number, fav: boolean) => client.request(`/app/orders/${id}`, { method: 'PATCH', body: { fav } }),
  saveNotifications: (version: string, notifications: Partial<NotificationFlags>) =>
    client.request<{ notifications: NotificationFlags; notifications_version: string }>('/app/settings', {
      method: 'PATCH', body: { notifications }, ifMatch: `"${version}"`,
    }),
  savePreferences: (version: string, changes: { lang?: string; currency?: string }) =>
    client.request('/app/me/prefs', { method: 'PATCH', body: changes, ifMatch: `"${version}"` }),
  createExport: (key: string) => client.request<AccountExport>('/app/account/export', { method: 'POST', idempotency: key }),
  exportStatus: (id: string, signal?: AbortSignal) => client.request<AccountExport>(`/app/account/operations/${id}`, { signal }),
  exportDownload: (id: string) => client.download(`/app/account/operations/${id}/download`),
  erase: (key: string) => client.request<AccountErasure>('/app/account', { method: 'DELETE', idempotency: key }),
  erasureStatus: (id: string, receipt: string, signal?: AbortSignal) =>
    client.request<AccountErasure>(`/app/account/operations/${id}`, { erasureReceipt: receipt, signal }),
  profile: (signal?: AbortSignal) => client.request<ProfileResponse>('/app/profile', { signal }),
  settings: (signal?: AbortSignal) => client.request<SettingsResponse>('/app/settings', { signal }),
  saveSettings: (version: string, patch: SettingsPatch, signal?: AbortSignal) =>
    client.request<SettingsResponse>('/app/settings', {
      method: 'PATCH', body: patch, ifMatch: `"${version}"`, signal,
    }),
});

export interface NotificationFlags { done: boolean; rewards: boolean; apps: boolean; news: boolean }
export interface AccountExport { operation_id: string; status: string; download_url: string | null; expires_at: string | null; manifest?: { scope?: string; excluded?: string[]; partial?: boolean; sources?: Array<{ provider: string; status: string }> } }
export interface AccountErasure { operation_id: string; status: string; status_receipt?: string; steos_id_preserved: boolean; financial_receipts_preserved: boolean }
export interface ProfileWork { id: number; kind: string; screen: string; status: string; fav: boolean }
export type { RewardsResponse } from './rewards';
export interface NewsItem { id: string; title: string; text?: string; description?: string; screen?: string }
export interface NewsRelease { version?: string; date?: string; summary?: string; items?: Array<NewsItem | string>; groups?: Array<{ id?: string; title: string; items: Array<NewsItem | string> }> }
export type ProfileBalance = Balance & { legacy_tokens?: string; batches_truncated?: boolean };
